import { useState, useRef, useEffect, type ReactNode } from "react";
import Markdown from "react-markdown";
import { usePreLessonQuestionsStore } from "../../hooks/use-pre-lesson-questions-store";
import { Send, Loader2, X, MessageSquare } from "lucide-react";
import { cn } from "../../lib/utils";

interface TeacherChatInterfaceProps {
  preLessonId: number;
}

const TeacherChatInterface = ({ preLessonId }: TeacherChatInterfaceProps) => {
  const {
    clarifyMessages,
    sendClarifyMessage,
    isSendingClarify,
    exitClarify,
    activeClarifyQuestion,
  } = usePreLessonQuestionsStore();

  const [input, setInput] = useState("");
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [clarifyMessages]);

  useEffect(() => {
    if (activeClarifyQuestion && inputRef.current) {
      inputRef.current.focus();
    }
  }, [activeClarifyQuestion]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || isSendingClarify) return;

    const message = input.trim();
    setInput("");
    await sendClarifyMessage(preLessonId, message);
  };

  if (!activeClarifyQuestion) return null;

  return (
    <div
      className={cn(
        "rounded-xl overflow-hidden border border-teal-200 bg-teal-50/30 w-full",
      )}
    >
      <div
        className={cn(
          "px-4 py-3 border-b bg-teal-50 border-teal-100 flex items-center justify-between",
        )}
      >
        <div className="flex items-center gap-2">
          <MessageSquare className="h-5 w-5 text-teal-600" />
          <div>
            <h3 className="font-semibold text-sm text-teal-900">
              Teaching ideas
            </h3>
            <p className="text-xs text-teal-600 truncate max-w-xs">
            </p>
          </div>
        </div>
        <button
          onClick={exitClarify}
          className="p-1.5 rounded-md hover:bg-black/5 transition-colors"
        >
          <X className="h-4 w-4 text-gray-500" />
        </button>
      </div>

      <div className="h-72 overflow-y-auto p-4 space-y-3 bg-white/50">
        {clarifyMessages.length === 0 && !isSendingClarify && (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <MessageSquare className="h-10 w-10 mb-2 text-teal-300" />
            <p className="text-gray-500 text-sm">
              Ask for clarification about this question
            </p>
          </div>
        )}
        {clarifyMessages.map((msg, index) => (
          <div
            key={index}
            className={cn(
              "flex",
              msg.role === "user" ? "justify-end" : "justify-start",
            )}
          >
            <div
              className={cn(
                "max-w-[80%] rounded-lg px-3 py-2 text-sm",
                msg.role === "user"
                  ? "bg-teal-500 text-white"
                  : "bg-gray-100 text-gray-800",
              )}
            >
              {msg.role === "user" ? (
                <p className="whitespace-pre-wrap leading-relaxed">
                  {msg.content}
                </p>
              ) : (
                <Markdown
                  components={{
                    p: ({ children }: { children?: ReactNode }) => (
                      <p className="mb-2 last:mb-0 leading-relaxed">
                        {children}
                      </p>
                    ),
                    ul: ({ children }: { children?: ReactNode }) => (
                      <ul className="list-disc pl-4 mb-2 space-y-1">
                        {children}
                      </ul>
                    ),
                    ol: ({ children }: { children?: ReactNode }) => (
                      <ol className="list-decimal pl-4 mb-2 space-y-1">
                        {children}
                      </ol>
                    ),
                    li: ({ children }: { children?: ReactNode }) => (
                      <li className="leading-relaxed">{children}</li>
                    ),
                    strong: ({ children }: { children?: ReactNode }) => (
                      <strong className="font-semibold">{children}</strong>
                    ),
                    em: ({ children }: { children?: ReactNode }) => (
                      <em className="italic">{children}</em>
                    ),
                    code: ({ children }: { children?: ReactNode }) => (
                      <code className="bg-gray-200 px-1 rounded text-xs">
                        {children}
                      </code>
                    ),
                  }}
                >
                  {msg.content}
                </Markdown>
              )}
            </div>
          </div>
        ))}
        {isSendingClarify && (
          <div className="flex justify-start">
            <div className="bg-gray-100 rounded-lg px-3 py-2">
              <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      <form
        onSubmit={handleSubmit}
        className={cn("p-3 border-t flex gap-2 border-teal-100 bg-teal-50/50")}
      >
        <input
          ref={inputRef}
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Ask for clarification..."
          disabled={isSendingClarify}
          className={cn(
            "flex-1 h-10 px-3 rounded-lg border bg-white text-sm focus:outline-none focus:ring-2 transition-all",
            "border-teal-200 focus:border-teal-300 focus:ring-teal-100",
          )}
        />
        <button
          type="submit"
          disabled={isSendingClarify || !input.trim()}
          className={cn(
            "w-10 h-10 rounded-lg flex items-center justify-center disabled:opacity-50 transition-colors",
            "bg-teal-500 hover:bg-teal-600 text-white",
          )}
        >
          {isSendingClarify ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Send className="h-4 w-4" />
          )}
        </button>
      </form>
    </div>
  );
};

export default TeacherChatInterface;
